// ============= Imports =============
// External dependencies
import { useCallback } from 'react';

// Internal dependencies - APIs
import { getFeeEarners } from '../../../../../apis/graph/get/getFeeEarners';
import { getPartners } from '../../../../../apis/graph/get/getPartners';
import { getUsers } from '../../../../../apis/graph/get/getUsers';
import { GraphDataHook } from '../../types/api/graph.types';
import { UseAuth } from '../../../../../contexts/AuthContext';

// ============= Types =============
/**
 * Type for the Graph API response data
 * @template T - Type of the value property in the response
 */

// ============= Hook Implementation =============
/**
 * Custom hook for handling Graph API data fetching operations
 * Provides a wrapped function to fetch user data from Microsoft Graph API
 *
 * @param getAccessToken - Function to retrieve authentication token
 * @returns Object containing the fetchGraphData function
 */
export const useGraphData =
  (): // getAccessToken: AuthContextProps['getAccessToken']
  GraphDataHook => {
    const { getAccessToken, spoofedUser } = UseAuth();
    /**
     * Fetches user data from Graph API based on search term
     *
     * @param searchTerm - Term to search for in Graph API
     * @returns Promise resolving to user data or null if unsuccessful
     *
     * @example
     * ```typescript
     * const { fetchGraphData } = useGraphData(getAccessToken);
     * const users = await fetchGraphData('john.doe');
     * ```
     */

    const fetchFeeEarners = useCallback(
      async (searchTerm: string, office?: string) => {
        // Return early if no search term provided
        if (!searchTerm) {
          return null;
        }

        try {
          // Fetch user data from Graph API
          const graphResult = await getFeeEarners({
            getAccessToken,
            searchValue: searchTerm,
            spoofedUserEmail: spoofedUser?.value,
            office,
          });

          // Return data if successful
          if (graphResult.success && graphResult.data) {
            return graphResult.data;
          }

          return null;
        } catch (error: unknown) {
          console.error('Error fetching protected data:', error);
          return null;
        }
      },
      []
    );

    const fetchPartners = useCallback(
      async (searchTerm: string, office?: string) => {
        // Return early if no search term provided
        if (!searchTerm) {
          return null;
        }

        try {
          // Fetch user data from Graph API
          const graphResult = await getPartners({
            getAccessToken,
            searchValue: searchTerm,
            spoofedUserEmail: spoofedUser?.value,
            office,
          });

          // Return data if successful
          if (graphResult.success && graphResult.data) {
            return graphResult.data;
          }

          return null;
        } catch (error: unknown) {
          console.error('Error fetching protected data:', error);
          return null;
        }
      },
      []
    );

    const fetchUsers = useCallback(
      async (searchTerm: string, office?: string) => {
        // Return early if no search term provided
        if (!searchTerm) {
          return null;
        }

        try {
          // Fetch user data from Graph API
          const graphResult = await getUsers({
            getAccessToken,
            searchValue: searchTerm,
            spoofedUserEmail: spoofedUser?.value,
            office,
          });

          // Return data if successful
          if (graphResult.success && graphResult.data) {
            return graphResult.data;
          }

          return null;
        } catch (error: unknown) {
          console.error('Error fetching protected data:', error);
          return null;
        }
      },
      []
    );

    return { fetchFeeEarners, fetchPartners, fetchUsers };
  };
